/**
 * Restore Pre-flight Badge (Spec 204 — US4 / FR-006)
 *
 * Runs pre-flight checks automatically when a restore request is formed and
 * surfaces the result as a collapsed/expandable badge set. The parent restore
 * launch modal should disable the "Restore" button while !passed.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 204-instant-granular-recovery
 * Constitution: v1.22.0 | Principle XLV (Design System)
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui';
import { mountApi, BackupEntityType, PreflightResult } from '../../api/restore';

const CHECK_LABELS: Record<string, string> = {
  workspace_reachable:  'Workspace reachable',
  sp_has_member_role:   'Backup identity has access',
  name_available:       'Target name available',
  capacity_active:      'Capacity active',
  tables_in_manifest:   'All tables in backup',
  unknown:              'Unknown check',
};

interface RestorePreflightBadgeProps {
  entityType: BackupEntityType;
  entityId: string;
  runId: string;
  targetWorkspaceId: string;
  tablesToRestore?: string[];
  /** Called when preflight result changes so parent can disable the launch button. */
  onResult?: (passed: boolean) => void;
}

export const RestorePreflightBadge: React.FC<RestorePreflightBadgeProps> = ({
  entityType, entityId, runId, targetWorkspaceId, tablesToRestore, onResult,
}) => {
  const [expanded, setExpanded] = useState(false);

  const enabled = !!(entityType && entityId && runId && targetWorkspaceId);

  const { data, isLoading, isFetching } = useQuery<PreflightResult>({
    queryKey: ['preflight', entityType, entityId, runId, targetWorkspaceId, tablesToRestore?.join(',') ?? ''],
    queryFn: () => mountApi.preflight(entityType, entityId, runId, targetWorkspaceId, tablesToRestore),
    enabled,
    staleTime: 30_000,
  });

  // Auto-expand when there are failures
  useEffect(() => {
    if (data && !data.passed) setExpanded(true);
  }, [data?.passed]);

  useEffect(() => {
    if (data) onResult?.(data.passed);
  }, [data?.passed]);

  if (!enabled) return null;

  return (
    <div className="rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-2">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex items-center gap-2">
          {(isLoading || isFetching) && (
            <Loader2 className="h-4 w-4 animate-spin text-[var(--color-text-secondary)]" />
          )}
          {!isLoading && !isFetching && data && (
            data.passed
              ? <CheckCircle2 className="h-4 w-4 text-[var(--color-success,#16a34a)]" />
              : <XCircle className="h-4 w-4 text-[var(--color-danger)]" />
          )}
          <span className="text-xs font-medium text-[var(--color-text-primary)]">
            {isLoading ? 'Checking…' : data ? (data.passed ? 'Ready to restore' : 'Pre-flight issues') : 'Pre-flight'}
          </span>
        </div>
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>

      {expanded && data && (
        <div className="mt-2 space-y-1.5 border-t border-[var(--color-border)] pt-2">
          {data.checks.map((check) => (
            <div key={check.name} className="flex items-start gap-2">
              {check.passed
                ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--color-success,#16a34a)]" />
                : <XCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--color-danger)]" />
              }
              <div className="min-w-0">
                <span className="text-xs text-[var(--color-text-primary)]">
                  {CHECK_LABELS[check.name] ?? check.name}
                </span>
                {!check.passed && check.detail && (
                  <p className="mt-0.5 text-xs text-[var(--color-danger)]">{check.detail}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
